import { ChevronDown, ChevronsLeft, House, type LucideIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import {
  filterSectionGroups,
  isParentItem,
  sectionGroups,
  type SectionGroup,
  type SectionItem,
} from '@/config/navigation'
import { usePermissions } from '@/hooks/use-permissions'
import { getAppVersion } from '@/lib/version'
import { useAuthStore } from '@/stores/auth-store'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface TopLevelNavItem {
  name: string
  path: string
  icon: LucideIcon
}

const topLevelNavItems: TopLevelNavItem[] = [{ name: 'Dashboard', path: '/', icon: House }]

function NavigationSkeleton() {
  return (
    <div className="px-3 space-y-6">
      <div className="space-y-1">
        <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
      </div>

      <div className="space-y-3">
        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="space-y-1">
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
        </div>
      </div>

      <div className="space-y-3">
        <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="space-y-1">
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse" />
        </div>
      </div>
    </div>
  )
}

function getNavLinkClassName(isActive: boolean, isCollapsed: boolean, isChild = false): string {
  const base = 'flex items-center py-2 text-sm font-medium rounded-md transition-colors relative'
  const paddingX = isChild && !isCollapsed ? 'pl-9 pr-3' : 'px-3'
  const activeClass =
    'text-primary-600 dark:text-primary-400 before:absolute before:left-0 before:top-1 before:bottom-1 before:w-1 before:bg-primary-600 dark:before:bg-primary-400 before:rounded-r-full'
  const inactiveClass =
    'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50'
  const layoutClass = isCollapsed ? 'justify-center' : 'gap-3'
  return `${base} ${paddingX} ${isActive ? activeClass : inactiveClass} ${layoutClass}`
}

function getParentClassName(isActive: boolean, isCollapsed: boolean): string {
  const base =
    'flex items-center w-full py-2 text-sm font-medium rounded-md transition-colors relative'
  const paddingX = isCollapsed ? 'px-3' : 'pl-3 pr-2'
  const activeClass =
    'text-primary-600 dark:text-primary-400 before:absolute before:left-0 before:top-1 before:bottom-1 before:w-1 before:bg-primary-600 dark:before:bg-primary-400 before:rounded-r-full'
  const inactiveClass =
    'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50'
  const layoutClass = isCollapsed ? 'justify-center' : 'justify-between gap-3'
  return `${base} ${paddingX} ${isActive ? activeClass : inactiveClass} ${layoutClass}`
}

function isChildPathActive(item: SectionItem, currentPath: string): boolean {
  if (item.path && currentPath.startsWith(item.path)) return true
  if (isParentItem(item)) {
    return item.children.some((child) => isChildPathActive(child, currentPath))
  }
  return false
}

function SidebarTopLevelLink({
  item,
  isCollapsed,
  onClose,
}: {
  item: TopLevelNavItem
  isCollapsed: boolean
  onClose: () => void
}) {
  const Icon = item.icon

  return (
    <NavLink
      to={item.path}
      onClick={onClose}
      className={({ isActive }) => getNavLinkClassName(isActive, isCollapsed)}
      title={isCollapsed ? item.name : undefined}
    >
      <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
      {!isCollapsed && <span>{item.name}</span>}
    </NavLink>
  )
}

function SidebarLeafLink({
  item,
  isCollapsed,
  isChild,
  onClose,
}: {
  item: SectionItem & { path: string }
  isCollapsed: boolean
  isChild: boolean
  onClose: () => void
}) {
  const Icon = item.icon
  const showIcon = isCollapsed || !isChild

  return (
    <NavLink
      to={item.path}
      onClick={onClose}
      className={({ isActive }) => getNavLinkClassName(isActive, isCollapsed, isChild)}
      title={isCollapsed ? item.name : undefined}
    >
      {showIcon && <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />}
      {!isCollapsed && <span>{item.name}</span>}
    </NavLink>
  )
}

function SidebarParentItem({
  item,
  isCollapsed,
  isExpanded,
  isAnyChildActive,
  expandedParents,
  onToggle,
  onToggleParent,
  onClose,
}: {
  item: SectionItem & { children: SectionItem[] }
  isCollapsed: boolean
  isExpanded: boolean
  isAnyChildActive: boolean
  expandedParents: Record<string, boolean>
  onToggle: () => void
  onToggleParent: (name: string) => void
  onClose: () => void
}) {
  const Icon = item.icon
  const showChildren = isExpanded && !isCollapsed
  const childrenId = `sidebar-children-${item.name.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className={getParentClassName(isAnyChildActive, isCollapsed)}
        title={isCollapsed ? item.name : undefined}
        aria-expanded={isExpanded}
        aria-controls={childrenId}
      >
        <div className={`flex items-center ${isCollapsed ? '' : 'gap-3'}`}>
          <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          {!isCollapsed && <span>{item.name}</span>}
        </div>
        {!isCollapsed && (
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
            aria-hidden="true"
          />
        )}
      </button>
      {showChildren && (
        <div id={childrenId} className="mt-1 space-y-1">
          {item.children.map((child) => (
            <SidebarSectionItem
              key={child.path ?? child.name}
              item={child}
              isCollapsed={isCollapsed}
              isChild={true}
              expandedParents={expandedParents}
              onToggleParent={onToggleParent}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function SidebarSectionItem({
  item,
  isCollapsed,
  isChild,
  expandedParents,
  onToggleParent,
  onClose,
}: {
  item: SectionItem
  isCollapsed: boolean
  isChild: boolean
  expandedParents: Record<string, boolean>
  onToggleParent: (name: string) => void
  onClose: () => void
}) {
  const { pathname } = useLocation()

  if (isParentItem(item)) {
    const isExpanded = expandedParents[item.name] ?? false
    return (
      <SidebarParentItem
        item={item}
        isCollapsed={isCollapsed}
        isExpanded={isExpanded}
        isAnyChildActive={isChildPathActive(item, pathname)}
        expandedParents={expandedParents}
        onToggle={() => {
          onToggleParent(item.name)
        }}
        onToggleParent={onToggleParent}
        onClose={onClose}
      />
    )
  }

  if (!item.path) return null

  return (
    <SidebarLeafLink
      item={item as SectionItem & { path: string }}
      isCollapsed={isCollapsed}
      isChild={isChild}
      onClose={onClose}
    />
  )
}

function collectParentNames(groups: SectionGroup[]): string[] {
  const names: string[] = []
  for (const group of groups) {
    for (const item of group.items) {
      if (isParentItem(item)) names.push(item.name)
    }
  }
  return names
}

function buildAutoExpandedMap(
  groups: SectionGroup[],
  currentPath: string,
  manuallyToggled: Record<string, boolean>
): Record<string, boolean> {
  const result: Record<string, boolean> = {}
  for (const name of collectParentNames(groups)) {
    if (name in manuallyToggled) {
      // eslint-disable-next-line security/detect-object-injection
      result[name] = manuallyToggled[name]
      continue
    }
    const parent = findParent(groups, name)
    if (parent && isChildPathActive(parent, currentPath)) {
      // eslint-disable-next-line security/detect-object-injection
      result[name] = true
    }
  }
  return result
}

function findParent(groups: SectionGroup[], name: string): SectionItem | null {
  for (const group of groups) {
    for (const item of group.items) {
      if (item.name === name && isParentItem(item)) return item
    }
  }
  return null
}

function GroupHeader({
  title,
  isCollapsed,
  isExpanded,
  onToggle,
}: {
  title: string
  isCollapsed: boolean
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center w-full px-3 py-2 mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors ${
        isCollapsed ? 'justify-center' : 'justify-between'
      }`}
    >
      {isCollapsed ? (
        <span className="text-lg border-b border-gray-400 dark:border-gray-500">{title[0]}</span>
      ) : (
        <>
          <span>{title}</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
            aria-hidden="true"
          />
        </>
      )}
    </button>
  )
}

function SidebarFooter({
  isCollapsed,
  onToggleCollapse,
}: {
  isCollapsed: boolean
  onToggleCollapse: () => void
}) {
  return (
    <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
      {!isCollapsed && (
        <div className="px-3 pt-3 pb-2">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center font-mono">
            {getAppVersion()}
          </p>
        </div>
      )}
      <div className="p-3">
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex items-center justify-center w-full p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronsLeft
            className={`h-4 w-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  )
}

function SidebarOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-50 z-20 lg:hidden"
      role="button"
      tabIndex={0}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClose()
        }
      }}
      aria-label="Close sidebar"
    />
  )
}

function SidebarGroupSection({
  group,
  isCollapsed,
  isExpanded,
  expandedParents,
  onToggle,
  onToggleParent,
  onClose,
}: {
  group: SectionGroup
  isCollapsed: boolean
  isExpanded: boolean
  expandedParents: Record<string, boolean>
  onToggle: () => void
  onToggleParent: (name: string) => void
  onClose: () => void
}) {
  return (
    <div className="px-3 mb-6">
      <GroupHeader
        title={group.title}
        isCollapsed={isCollapsed}
        isExpanded={isExpanded}
        onToggle={onToggle}
      />
      {isExpanded && (
        <div className="space-y-1">
          {group.items.map((item) => (
            <SidebarSectionItem
              key={item.path ?? item.name}
              item={item}
              isCollapsed={isCollapsed}
              isChild={false}
              expandedParents={expandedParents}
              onToggleParent={onToggleParent}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function useParentExpansion(filteredGroups: SectionGroup[]) {
  const { pathname } = useLocation()
  const [manuallyToggled, setManuallyToggled] = useState<Record<string, boolean>>({})

  const expandedParents = useMemo(
    () => buildAutoExpandedMap(filteredGroups, pathname, manuallyToggled),
    [filteredGroups, pathname, manuallyToggled]
  )

  function toggleParent(name: string) {
    setManuallyToggled((prev) => {
      // eslint-disable-next-line security/detect-object-injection
      const current = expandedParents[name] ?? false
      return { ...prev, [name]: !current }
    })
  }

  return { expandedParents, toggleParent }
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { hasAccess, user } = usePermissions()
  const { isLoading } = useAuthStore()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(sectionGroups.map((g) => [g.key, true]))
  )

  const filteredGroups = user ? filterSectionGroups(user, hasAccess) : []
  const { expandedParents, toggleParent } = useParentExpansion(filteredGroups)

  const toggleGroup = (groupKey: string) => {
    // eslint-disable-next-line security/detect-object-injection
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))
  }

  return (
    <>
      {isOpen && <SidebarOverlay onClose={onClose} />}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-all duration-200 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isCollapsed ? 'w-16' : 'w-64'}`}
      >
        <div className="h-full flex flex-col">
          <nav className="flex-1 overflow-y-auto py-4">
            {isLoading || !user ? (
              <NavigationSkeleton />
            ) : (
              <>
                <div className="px-3 mb-6">
                  <div className="space-y-1">
                    {topLevelNavItems.map((item) => (
                      <SidebarTopLevelLink
                        key={item.path}
                        item={item}
                        isCollapsed={isCollapsed}
                        onClose={onClose}
                      />
                    ))}
                  </div>
                </div>

                {filteredGroups.map((group) => (
                  <SidebarGroupSection
                    key={group.key}
                    group={group}
                    isCollapsed={isCollapsed}
                    isExpanded={expandedGroups[group.key]}
                    expandedParents={expandedParents}
                    onToggle={() => {
                      toggleGroup(group.key)
                    }}
                    onToggleParent={toggleParent}
                    onClose={onClose}
                  />
                ))}
              </>
            )}
          </nav>

          <SidebarFooter
            isCollapsed={isCollapsed}
            onToggleCollapse={() => {
              setIsCollapsed(!isCollapsed)
            }}
          />
        </div>
      </aside>
    </>
  )
}
